import { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TextInput,
  MultiSelect,
  Group,
  Text,
  Anchor,
  ActionIcon,
  Box,
  Paper,
  Title,
  Stack,
  LoadingOverlay,
  Select,
  Center,
  UnstyledButton,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconX, IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { fetchLawyersTable, updateLawyerField } from '../../services/lawyerFetches';
import { fetchExpertise } from '@/services/proposalsRoutes';
import useLocalStorage from '@/hooks/useLocalStorage';

interface Lawyer {
  LawyerName: string;
  ProfessionalTitleEn: string | null;
  ProfessionalTitlePl: string | null;
  PositionEn: string | null;
  PositionPl: string | null;
  Expertise: string[];
}

type LawyerField = keyof Lawyer;

type SortField =
  | 'LawyerName'
  | 'ProfessionalTitleEn'
  | 'ProfessionalTitlePl'
  | 'PositionEn'
  | 'PositionPl'
  | 'Expertise';
type SortDirection = 'asc' | 'desc';

const filterType = [
  'LawyerName',
  'ProfessionalTitleEn',
  'ProfessionalTitlePl',
  'PositionEn',
  'PositionPl',
  'Expertise',
] as const;

type FilterType = (typeof filterType)[number];

interface BaseFilter {
  type: Exclude<FilterType, 'Expertise'>;
  value: string;
}

interface ExpertiseFilter {
  type: 'Expertise';
  value: string[];
}

type Filter = BaseFilter | ExpertiseFilter;

export default function LawyerTable() {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  /** ─────────────────────────  local state  ───────────────────────── */
  const [lawyers, setLawyers] = useState<Lawyer[]>([]);
  const [loading, setLoading] = useState(true);
  const [expertise, setExpertise] = useState<string[]>([]);

  const [filter, setFilter] = useLocalStorage<Filter>('lawyerTable.filter', {
    type: 'LawyerName',
    value: '',
  });

  const [sortField, setSortField] = useLocalStorage<SortField>('lawyerTable.sortField', 'LawyerName');
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('lawyerTable.sortDirection', 'asc');

  /** in-place editing */
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    field: LawyerField;
  } | null>(null);
  const [editValue, setEditValue] = useState<string | string[]>('');

  /* ─────────────────────────  fetch data  ───────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const data = await fetchLawyersTable(token);
        setLawyers(data);
      } catch (err) {
        console.error(err);
        notifications.show({
          title: t('error'),
          message: t('failedToFetchLawyers'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [getAccessTokenSilently, t]);

  useEffect(() => {
    async function getExpertise() {
      const accessToken = await getAccessTokenSilently();
      const response = await fetchExpertise(accessToken);
      setExpertise(response);
    }
    getExpertise();
  }, []);

  /* ─────────────────────────  filtered rows  ───────────────────────── */
  const filteredLawyers = useMemo(() => {
    return lawyers.filter((lawyer) => {
      if (!filter.value || (Array.isArray(filter.value) && !filter.value.length)) {
        return true;
      }

      switch (filter.type) {
        case 'LawyerName':
          return lawyer.LawyerName.toLowerCase().includes((filter.value as string).toLowerCase());

        case 'ProfessionalTitleEn':
          return (
            lawyer.ProfessionalTitleEn?.toLowerCase().includes(
              (filter.value as string).toLowerCase()
            ) ?? false
          );

        case 'ProfessionalTitlePl':
          return (
            lawyer.ProfessionalTitlePl?.toLowerCase().includes(
              (filter.value as string).toLowerCase()
            ) ?? false
          );

        case 'PositionEn':
          return (
            lawyer.PositionEn?.toLowerCase().includes((filter.value as string).toLowerCase()) ??
            false
          );

        case 'PositionPl':
          return (
            lawyer.PositionPl?.toLowerCase().includes((filter.value as string).toLowerCase()) ??
            false
          );

        case 'Expertise':
          return (filter.value as string[]).every((exp) => lawyer.Expertise?.includes(exp));

        default:
          return true;
      }
    });
  }, [lawyers, filter]);

  /* ─────────────────────────  sorting  ───────────────────────── */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedLawyers = useMemo(() => {
    const arr = [...filteredLawyers];

    const compare = (aStr: string, bStr: string, locale: 'en' | 'pl' = 'en') =>
      aStr.localeCompare(bStr, locale, { sensitivity: 'base', numeric: true });

    arr.sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'LawyerName':
          result = compare(a.LawyerName ?? '', b.LawyerName ?? '', 'pl');
          break;
        case 'ProfessionalTitleEn':
          result = compare(a.ProfessionalTitleEn ?? '', b.ProfessionalTitleEn ?? '', 'en');
          break;
        case 'ProfessionalTitlePl':
          result = compare(a.ProfessionalTitlePl ?? '', b.ProfessionalTitlePl ?? '', 'pl');
          break;
        case 'PositionEn':
          result = compare(a.PositionEn ?? '', b.PositionEn ?? '', 'en');
          break;
        case 'PositionPl':
          result = compare(a.PositionPl ?? '', b.PositionPl ?? '', 'pl');
          break;
        case 'Expertise': {
          const aExp = (a.Expertise && a.Expertise.length ? a.Expertise.join(', ') : '') ?? '';
          const bExp = (b.Expertise && b.Expertise.length ? b.Expertise.join(', ') : '') ?? '';
          result = compare(aExp, bExp, 'en');
          break;
        }
        default:
          result = 0;
      }
      return sortDirection === 'asc' ? result : -result;
    });

    return arr;
  }, [filteredLawyers, sortField, sortDirection]);

  /* ─────────────────────────  edit helpers  ───────────────────────── */
  const beginEdit = (rowIndex: number, field: LawyerField) => {
    const current = sortedLawyers[rowIndex][field];
    setEditingCell({ rowIndex, field });
    setEditValue(field === 'Expertise' ? [...(current as string[])] : (current ?? '').toString());
  };

  const cancelEdit = () => setEditingCell(null);

  const saveEdit = async () => {
    if (!editingCell) return;

    const { rowIndex, field } = editingCell;
    const lawyer = sortedLawyers[rowIndex];
    const newValue = field === 'Expertise' ? (editValue as string[]) : (editValue as string).trim();

    try {
      const token = await getAccessTokenSilently();
      await updateLawyerField(token, lawyer.LawyerName, field, newValue);

      // update local cache
      setLawyers((prev) =>
        prev.map((l) => (l.LawyerName === lawyer.LawyerName ? { ...l, [field]: newValue } : l))
      );

      notifications.show({
        title: t('success'),
        message: t('lawyerUpdated'),
        color: 'green',
      });
    } catch (err) {
      console.error(err);
      notifications.show({
        title: t('error'),
        message: t('failedToUpdateLawyer'),
        color: 'red',
      });
    }

    setEditingCell(null);
  };

  /* ─────────────────────────  cell renderer  ───────────────────────── */
  const renderCell = (lawyer: Lawyer, field: LawyerField, rowIndex: number) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      const input =
        field === 'Expertise' ? (
          <MultiSelect
            data={expertise}
            value={editValue as string[]}
            onChange={setEditValue}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveEdit();
              }
            }}
          />
        ) : (
          <TextInput
            value={editValue as string}
            onChange={(e) => setEditValue(e.currentTarget.value)}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveEdit();
              }
            }}
          />
        );

      return (
        <Stack gap="xs" fz="sm">
          {input}
          <Group gap="xs" wrap="nowrap">
            <ActionIcon color="green" onClick={saveEdit}>
              <IconCheck size={16} />
            </ActionIcon>
            <ActionIcon color="red" onClick={cancelEdit}>
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Stack>
      );
    }

    /** non-editing view */
    if (field === 'LawyerName') {
      return (
        <Text fz="sm">
          <Anchor component={Link} to={`/editlawyer/${lawyer.LawyerName}`}>{lawyer.LawyerName}</Anchor>
        </Text>
      );
    }

    return (
      <Text fz="sm" style={{ cursor: 'pointer' }} onClick={() => beginEdit(rowIndex, field)}>
        {field === 'Expertise'
          ? lawyer.Expertise
            ? lawyer.Expertise.join(', ')
            : '—'
          : (lawyer[field] ?? '—')}
      </Text>
    );
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    const icon = isActive ? (sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />) : null;

    return (
      <Table.Th>
        <UnstyledButton onClick={() => handleSort(field)}>
          <Group gap={4} justify="space-between">
            <Text fw={500}>{children}</Text>
            {icon}
          </Group>
        </UnstyledButton>
      </Table.Th>
    );
  };

  /* ─────────────────────────  render  ───────────────────────── */
  return (
    <Center>
      <Box p="md" w="80%" style={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} />

        <Title order={2} mb="md">
          {t('lawyerExpertiseTable')}
        </Title>

        {/* filters */}
        <Box>
          <Filter filter={filter} setFilter={setFilter} expertise={expertise} />

          {/* table */}
          <Table striped highlightOnHover withColumnBorders fz="sm">
            <Table.Thead>
              <Table.Tr>
                <SortableHeader field="LawyerName">{t('name')}</SortableHeader>
                <SortableHeader field="ProfessionalTitleEn">{t('professionalTitleEn')}</SortableHeader>
                <SortableHeader field="ProfessionalTitlePl">{t('professionalTitlePl')}</SortableHeader>
                <SortableHeader field="PositionEn">{t('positionEn')}</SortableHeader>
                <SortableHeader field="PositionPl">{t('positionPl')}</SortableHeader>
                <SortableHeader field="Expertise">{t('expertise')}</SortableHeader>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedLawyers.map((lawyer, idx) => (
                <Table.Tr key={lawyer.LawyerName}>
                  <Table.Td>{renderCell(lawyer, 'LawyerName', idx)}</Table.Td>
                  <Table.Td>{renderCell(lawyer, 'ProfessionalTitleEn', idx)}</Table.Td>
                  <Table.Td>{renderCell(lawyer, 'ProfessionalTitlePl', idx)}</Table.Td>
                  <Table.Td>{renderCell(lawyer, 'PositionEn', idx)}</Table.Td>
                  <Table.Td>{renderCell(lawyer, 'PositionPl', idx)}</Table.Td>
                  <Table.Td>{renderCell(lawyer, 'Expertise', idx)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Box>
    </Center>
  );
}

export function Filter({
  filter,
  setFilter,
  expertise,
}: {
  filter: Filter;
  setFilter: (filter: Filter) => void;
  expertise: string[];
}) {
  const { t } = useTranslation();

  const filterTypeMap = {
    LawyerName: t('name'),
    ProfessionalTitleEn: t('professionalTitleEn'),
    ProfessionalTitlePl: t('professionalTitlePl'),
    PositionEn: t('positionEn'),
    PositionPl: t('positionPl'),
    Expertise: t('expertise'),
  };

  return (
    <Group p="md" mb="md">
      <Select
        label={t('filterType')}
        data={filterType.map((type) => {
          return { value: type, label: filterTypeMap[type] };
        })}
        value={filter.type}
        onChange={(value) => {
          if (value === 'Expertise') {
            const tempFilter: ExpertiseFilter = {
              type: 'Expertise',
              value: [],
            };
            setFilter(tempFilter);
          } else {
            const tempFilter: BaseFilter = {
              type: value as Exclude<FilterType, 'Expertise'>,
              value: '',
            };
            setFilter(tempFilter);
          }
        }}
      />
      {filter.type === 'Expertise' && (
        <MultiSelect
          label={t('search')}
          data={expertise}
          value={filter.value as string[]}
          onChange={(value) => setFilter({ ...filter, value })}
          miw={300}
        />
      )}
      {filter.type !== 'Expertise' && (
        <TextInput
          label={t('search')}
          value={filter.value as string}
          onChange={(e) => setFilter({ ...filter, value: e.target.value })}
        />
      )}
    </Group>
  );
}
